import * as React from 'react';
import {RouteComponentProps, withRouter} from 'react-router';
import * as paths from '../../paths';
import {Attachment, ErrorResponse, IncomingInvoice, Partner, User} from '../../api/dtos/dtos';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../../app/ApplicationContext';
import IncomingInvoiceForm from './IncomingInvoiceForm';
import {ErrorAlert} from '../../common/ErrorAlert';
import furoApi from '../../api/FuroApi';
import {showDeleteSuccessNotification, showSaveSuccessNotification} from '../../utils/dialogs';
import locales from '../../locales';
import SectionHeaderFormItem from '../../common/form-items/SectionHeaderFormItem';
import InvoiceControlButtons from './InvoiceControlButtons';
import InvoiceItemsEditor from './InvoiceItemsEditor';
import FileUploadEditor from "../../common/input/FileUploadEditor";
import moment from "moment/moment";
import {now, toIsoDateString} from "../../utils/date-utils";
import {CURRENCY_EUR} from "../../utils/currency-utils";

interface State {
  editable: boolean;
  invoiceId: number;
  types: string[];
  partners: Partner[];
  users: User[];
  invoice?: IncomingInvoice;
  eurExchangeRate?: number;
  error?: ErrorResponse;
}

class IncomingInvoiceDetailsPage extends React.Component<RouteComponentProps<paths.IdPageUrlParams>, State> {
  public readonly loadingHandler?: LoadingOverlayHandler;
  public state: State = {
    editable: false,
    invoiceId: Number(this.props.match.params.id),
    types: [],
    partners: [],
    users: [],
  };

  public componentDidMount() {
    this.loadInvoiceTypes();
    this.loadInvoice();
    this.loadUsers();
  }

  public render() {
    const {editable, types, users, partners, invoice, eurExchangeRate, error} = this.state;

    const action = invoice?.id ? furoApi.incomingInvoices.uploadAttachmentsUrl(invoice.id) : undefined;

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <InvoiceControlButtons
          invoice={invoice}
          editable={editable}
          onEditClicked={this.onEditClicked}
          onDeleteConfirmed={this.deleteInvoice}
          onReopenConfirmed={this.reopenInvoice}
        />
        <IncomingInvoiceForm
          types={types}
          partners={partners}
          users={users.map(user => user.userName!)}
          invoice={invoice}
          editable={editable}
          defaultEurExchangeRate={eurExchangeRate}
          onRefreshDefaultExchangeRate={this.refreshDefaultExchangeRate}
          onPartnerSearch={this.searchPartner}
          onPartnerDelete={this.deletePartner}
          onFormSubmit={this.onFormSubmit}
          onFormCancel={this.onFormCancel}
        />

        {invoice && invoice.id &&
            <>
                <SectionHeaderFormItem title={locales.attachments} style={{marginTop: 48}}/>
                <FileUploadEditor
                    disabled={editable}
                    itemId={invoice.id}
                    action={action}
                    loadAttachment={this.loadAttachment}
                    deleteAttachment={this.deleteAttachment}
                />

                <SectionHeaderFormItem title={locales.items} style={{marginTop: 48}}/>
                <InvoiceItemsEditor disabled={editable} incomingInvoiceId={invoice.id}/>
            </>
        }
        <ErrorAlert style={{marginTop: '16px'}} error={error}/>
      </>
    );
  }

  private onEditClicked = () => {
    this.setState({editable: true});
  };

  private onFormCancel = () => {
    this.setState({editable: false});
  };

  private onFormSubmit = (invoice: IncomingInvoice) => {
    this.saveInvoice(invoice);
  };

  private loadAttachment = (id: number): Promise<Attachment[]> => {
    return furoApi.incomingInvoices.getAttachments(id);
  }

  private deleteAttachment = (id: number, attachmentId: number): Promise<void> => {
    return furoApi.incomingInvoices.deleteAttachment(id, String(attachmentId));
  }


  private searchPartner = (value: string) => {
    furoApi.incomingInvoices.searchPartners(value)
      .then((partners) => this.setState({partners}))
      .catch(this.onApiError);
  };

  private deletePartner = (id: number) => {
    furoApi.incomingInvoices.deletePartner(id)
      .then((_) => this.onPartnerDeleted(id))
      .catch(this.onApiError);
  };

  private loadInvoiceTypes = () => {
    const promise = furoApi.incomingInvoices.getTypes()
      .then((response) => this.setState({types: response.types}))
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private loadUsers = () => {
    const promise = furoApi.users.getUsers()
      .then((response) => this.setState({users: response}))
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private refreshDefaultExchangeRate = (date?: moment.Moment) => {
    if (!date || date.isAfter(now(), 'day')) {
      return;
    }
    return this.loadCurrentEurExchangeRate(date);
  };

  private loadCurrentEurExchangeRate = (d?: moment.Moment) => {
    const date = toIsoDateString(d ?? now());
    const promise = furoApi.currency.getRate(CURRENCY_EUR, date)
      .then((response) => {
        this.setState({eurExchangeRate: response.rate});
      })
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
    return promise;
  };

  private loadInvoice = () => {
    const promise = furoApi.incomingInvoices.get(this.state.invoiceId)
      .then((invoice) => this.setState({invoice, error: undefined}))
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private deleteInvoice = () => {
    const promise = furoApi.incomingInvoices.delete(this.state.invoiceId)
      .then(this.onInvoiceDeleted)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private reopenInvoice = () => {
    const promise = furoApi.incomingInvoices.reopen(this.state.invoiceId)
      .then(this.onInvoiceReopened)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private saveInvoice = (invoice: IncomingInvoice) => {
    const promise = furoApi.incomingInvoices.save(invoice, this.state.invoiceId)
      .then(this.onInvoiceSaved)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onInvoiceSaved = (invoice: IncomingInvoice) => {
    showSaveSuccessNotification();
    this.setInvoice(invoice);
    this.loadInvoiceTypes();
  };

  private onInvoiceReopened = (invoice: IncomingInvoice) => {
    showSaveSuccessNotification();
    this.setInvoice(invoice);
  };

  private onInvoiceDeleted = () => {
    showDeleteSuccessNotification();
    this.props.history.goBack();
  };

  private onPartnerDeleted = (id: number) => {
    showDeleteSuccessNotification();
    const {partners} = this.state;
    const idx = partners.findIndex(p => p.id === id);
    if (idx > -1) {
      partners.splice(idx, 1);
      this.setState({partners});
    }
  };

  private onApiError = (error: ErrorResponse) => this.setState({error});

  private setInvoice = (invoice: IncomingInvoice) => this.setState({editable: false, error: undefined, invoice});
}

export default withRouter(IncomingInvoiceDetailsPage);


// WEBPACK FOOTER //
// ./src/incoming-invoices/details/IncomingInvoiceDetailsPage.tsx